// Comunidades (lote 7, item 6): as chamadas à API e as mesas da comunidade, que vêm da mesa. Só os membros veem o
// mural, as mesas, o ranking, os eventos e a lista de membros; a ficha abre para quem tem o convite ou na busca.
import type { EstadoDivisao } from '@truco6/gamificacao';
import type { SalaNaLista } from '@truco6/mesa/protocolo';
import {
  COMUNIDADES_POR_DONO, DESCRICAO_DA_COMUNIDADE_MAX, IMAGEM_DO_POST_MAX_BYTES, MEMBROS_DA_COMUNIDADE_MAX, NOME_DA_COMUNIDADE_MAX, POST_MAX,
} from '@truco6/planos';
import { api } from './api.ts';
import { mesaHttp } from './config.ts';
import type { Sessao } from './conta.ts';

export const TIPOS_DE_COMUNIDADE = [['firma', 'Firma'], ['bar', 'Bar'], ['familia', 'Família'], ['clube', 'Clube'], ['turma', 'Turma']] as const;
export type TipoComunidade = (typeof TIPOS_DE_COMUNIDADE)[number][0];
export const NOME_DO_TIPO = Object.fromEntries(TIPOS_DE_COMUNIDADE) as Record<TipoComunidade, string>;
// Os limites ficam em @truco6/planos, com o servidor e os Termos.
export { COMUNIDADES_POR_DONO, NOME_DA_COMUNIDADE_MAX, POST_MAX };
export const DESCRICAO_MAX = DESCRICAO_DA_COMUNIDADE_MAX, IMAGEM_MAX_BYTES = IMAGEM_DO_POST_MAX_BYTES, MEMBROS_MAX = MEMBROS_DA_COMUNIDADE_MAX;
export const TITULO_EVENTO_MAX = 80;
export const REACOES_DO_MURAL = ['😂', '🔥', '👏', '😱', '🤡', '😎', '🙏', '💪'] as const;

export type Papel = 'dono' | 'moderador' | 'membro';
export interface ResumoComunidade {
  id: string; nome: string; escudo: string; tipo: TipoComunidade; cidade: string | null; descricao: string; publica: boolean; aprovacao: boolean;
  membros: number; somenteLeitura: boolean;
}
export interface EventoNaTela { id: string; titulo: string; quando: number; sala: string | null; campeonato: string | null }
export interface FichaComunidade extends ResumoComunidade {
  papel: Papel | null;
  pedido: boolean;
  convite: string | null; // só dono e moderadores
  dono: { usuario: string; apelido: string; avatar: string | null };
  avisoSemOuro: number | null;
  eventos: EventoNaTela[];
}
export interface MinhasComunidades {
  comunidades: (ResumoComunidade & { papel: Papel })[];
  convites: (ResumoComunidade & { de: string; deApelido: string; convite: string })[];
  podeCriar: boolean;
  criadas: number;
}
export interface MembroNaLista { usuario: string; papel: Papel; desde: number; apelido: string; avatar: string | null; divisao: EstadoDivisao | null }
export interface PedidoDeEntrada { usuario: string; apelido: string; avatar: string | null; em: number }
export interface PostNaTela {
  id: string; texto: string; imagem: string | null; criadoEm: number; autor: { usuario: string; apelido: string; avatar: string | null };
  reacoes: Record<string, number>; minha: string | null; podeApagar: boolean;
}
export interface LinhaDoRanking { posicao: number; usuario: string; apelido: string; avatar: string | null; partidas: number; vitorias: number; aproveitamento: number }
export type PeriodoDoRanking = 'semana' | 'mes' | 'temporada';

const c = (id: string) => `/comunidades/${id}`;
export const linkDaComunidade = (convite: string) => `${location.origin}/comunidade/${convite}`;

export const minhasComunidades = (s: Sessao) => api<MinhasComunidades>(s, 'GET', '/comunidades');
export const buscarComunidades = (s: Sessao, texto: string) => api<{ comunidades: ResumoComunidade[] }>(s, 'GET', `/comunidades/busca?q=${encodeURIComponent(texto)}`);
export const criarComunidade = (s: Sessao, b: { nome: string; escudo: string; tipo: TipoComunidade; cidadeIbge: number | null; descricao: string; publica: boolean; aprovacao: boolean }) =>
  api<FichaComunidade>(s, 'POST', '/comunidades', b);
export const lerComunidade = (s: Sessao, id: string) => api<FichaComunidade>(s, 'GET', c(id));
export const comunidadePorConvite = (s: Sessao, codigo: string) => api<FichaComunidade>(s, 'GET', `/comunidades/convite/${codigo}`);
export const editarComunidade = (s: Sessao, id: string, b: Partial<{ nome: string; descricao: string; escudo: string; publica: boolean; aprovacao: boolean }>) =>
  api<FichaComunidade>(s, 'POST', c(id) + '/editar', b);
export const entrarNaComunidade = (s: Sessao, id: string, convite: string | null) => api<{ entrou: boolean; pedido: boolean }>(s, 'POST', c(id) + '/entrar', { convite });
export const sairDaComunidade = (s: Sessao, id: string) => api<{ ok: true }>(s, 'POST', c(id) + '/sair');
export const lerMembros = (s: Sessao, id: string) => api<{ membros: MembroNaLista[]; pedidos: PedidoDeEntrada[] }>(s, 'GET', c(id) + '/membros');
export const decidirPedido = (s: Sessao, id: string, usuario: string, aprovar: boolean) =>
  api<{ ok: true }>(s, 'POST', `${c(id)}/pedidos/${encodeURIComponent(usuario)}/${aprovar ? 'aprovar' : 'recusar'}`);
export const definirPapel = (s: Sessao, id: string, usuario: string, papel: 'moderador' | 'membro') => api<{ ok: true }>(s, 'POST', c(id) + '/papel', { usuario, papel });
export const transferirComunidade = (s: Sessao, id: string, usuario: string) => api<{ ok: true }>(s, 'POST', c(id) + '/transferir', { usuario });
export const removerMembro = (s: Sessao, id: string, usuario: string) => api<{ ok: true }>(s, 'POST', c(id) + '/remover', { usuario });
export const novoConvite = (s: Sessao, id: string) => api<{ convite: string }>(s, 'POST', c(id) + '/convite/novo');
export const convidarAmigos = (s: Sessao, id: string, usuarios: string[]) => api<{ enviados: number }>(s, 'POST', c(id) + '/convidar', { usuarios });
export const denunciarComunidade = (s: Sessao, id: string) => api<{ ok: true }>(s, 'POST', c(id) + '/denunciar');
export const lerMural = (s: Sessao, id: string, antes: number | null = null) =>
  api<{ posts: PostNaTela[]; podePublicar: boolean }>(s, 'GET', c(id) + '/mural' + (antes ? `?antes=${antes}` : ''));
export const publicarNoMural = (s: Sessao, id: string, texto: string, imagem: string | null) => api<PostNaTela>(s, 'POST', c(id) + '/mural', { texto, imagem });
export const apagarPost = (s: Sessao, id: string, post: string) => api<{ ok: true }>(s, 'POST', `${c(id)}/mural/${post}/apagar`);
export const reagirAoPost = (s: Sessao, id: string, post: string, emoji: string | null) =>
  api<{ reacoes: Record<string, number>; minha: string | null }>(s, 'POST', `${c(id)}/mural/${post}/reagir`, { emoji });
export const denunciarPost = (s: Sessao, id: string, post: string) => api<{ ok: true }>(s, 'POST', `${c(id)}/mural/${post}/denunciar`);
export const lerRankingDaComunidade = (s: Sessao, id: string, periodo: PeriodoDoRanking) =>
  api<{ periodo: PeriodoDoRanking; linhas: LinhaDoRanking[] }>(s, 'GET', `${c(id)}/ranking?periodo=${periodo}`);
export const lerEventos = (s: Sessao, id: string) => api<{ eventos: EventoNaTela[]; podeCriar: boolean }>(s, 'GET', c(id) + '/eventos');
export const criarEvento = (s: Sessao, id: string, b: { titulo: string; quando: number; sala: string | null }) => api<EventoNaTela>(s, 'POST', c(id) + '/eventos', b);
export const cancelarEvento = (s: Sessao, id: string, evento: string) => api<{ ok: true }>(s, 'POST', `${c(id)}/eventos/${evento}/cancelar`);

// As mesas abertas da comunidade vêm da mesa, só para os membros.
export async function lerMesasDaComunidade(s: Sessao, id: string): Promise<SalaNaLista[]> {
  const r = await fetch(`${mesaHttp}/comunidades/${id}/salas`, { cache: 'no-store', headers: { authorization: 'Bearer ' + (await s.token()) } });
  if (!r.ok) throw new Error('Não deu para carregar as mesas da comunidade.');
  return ((await r.json()) as { salas: SalaNaLista[] }).salas;
}

// A imagem do mural vai como data URL. JPEG, PNG ou WebP até 1 MB, conferidos de novo no servidor.
export function lerImagem(arquivo: File): Promise<string> {
  return new Promise((ok, falha) => {
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(arquivo.type)) { falha(new Error('A imagem precisa ser JPEG, PNG ou WebP.')); return; }
    if (arquivo.size > IMAGEM_MAX_BYTES) { falha(new Error(`A imagem pode ter até ${IMAGEM_MAX_BYTES / 1_000_000} MB.`)); return; }
    const r = new FileReader();
    r.onload = () => ok(String(r.result));
    r.onerror = () => falha(new Error('Não deu para ler a imagem.'));
    r.readAsDataURL(arquivo);
  });
}

// "sex., 24 de out. · 20h30", no horário de Brasília.
export function quandoDoEvento(ms: number): string {
  const d = new Date(ms);
  const dia = d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo', weekday: 'short', day: 'numeric', month: 'short' });
  const [h, m] = d.toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit', hour12: false }).split(':');
  return `${dia} · ${Number(h)}h${m === '00' ? '' : m}`;
}

// Data e hora digitadas (horário de Brasília, sem horário de verão) para o instante.
export function instanteDe(data: string, hora: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(data) || !/^\d{2}:\d{2}$/.test(hora)) return null;
  const t = Date.parse(`${data}T${hora}:00-03:00`);
  return Number.isFinite(t) ? t : null;
}
